import React, { useEffect, useState } from 'react';
import { Recipe } from '../../types';
import './RecipeModal.css';

interface RecipeModalProps {
  recipe: Recipe | null;
  onClose: () => void;
}

const RecipeModal: React.FC<RecipeModalProps> = ({ recipe, onClose }) => {
  const [imgIndex, setImgIndex] = useState(0);

  useEffect(() => { setImgIndex(0); }, [recipe]);

  useEffect(() => {
    if (!recipe) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [recipe, onClose]);

  useEffect(() => {
    document.body.style.overflow = recipe ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [recipe]);

  if (!recipe) return null;

  const images = recipe.imageUrls ?? [];
  const hasImages = images.length > 0;
  const prev = () => setImgIndex(i => (i - 1 + images.length) % images.length);
  const next = () => setImgIndex(i => (i + 1) % images.length);

  const shareText = [
    `*${recipe.name}*`,
    recipe.yieldInfo ? `Yield: ${recipe.yieldInfo}` : '',
    recipe.ingredients?.length
      ? `\n*Ingredients:*\n${recipe.ingredients.map(ing => `• ${ing}`).join('\n')}`
      : '',
    recipe.instructions?.length
      ? `\n*Method:*\n${recipe.instructions.map((s, i) => `${i + 1}. ${s}`).join('\n')}`
      : '',
  ].filter(Boolean).join('\n');

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;


  return (
    <div
      className="rm-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={recipe.name}
      onClick={onClose}
    >
      <div className="rm-panel" onClick={e => e.stopPropagation()}>

        {/* ── Close ─────────────────────────── */}
        <button className="rm-close" onClick={onClose} aria-label="Close">×</button>

        {/* ── Name (centered, full width) ───── */}
        <div className="rm-header">
          <h2 className="rm-title">{recipe.name}</h2>
          {recipe.yieldInfo && <p className="rm-yield">{recipe.yieldInfo}</p>}
        </div>

        {/* ── Two-column body ───────────────── */}
        <div className="rm-body">

          {/* Left: image carousel */}
          <div className="rm-left">
            {hasImages ? (
              <div className="rm-carousel">
                <img
                  key={imgIndex}
                  src={images[imgIndex]}
                  alt={`${recipe.name} — ${imgIndex + 1} of ${images.length}`}
                  className="rm-carousel__img"
                />
                {images.length > 1 && (
                  <>
                    <button
                      className="rm-carousel__arrow rm-carousel__arrow--prev"
                      onClick={prev}
                      aria-label="Previous image"
                    >‹</button>
                    <button
                      className="rm-carousel__arrow rm-carousel__arrow--next"
                      onClick={next}
                      aria-label="Next image"
                    >›</button>
                    <span className="rm-carousel__counter">
                      {imgIndex + 1} / {images.length}
                    </span>
                  </>
                )}
              </div>
            ) : (
              <div className="rm-no-image" aria-hidden="true">🍲</div>
            )}
          </div>

          {/* Right: scrollable content */}
          <div className="rm-right">

            {recipe.ingredients && recipe.ingredients.length > 0 && (
              <div className="rm-section">
                <h3 className="rm-section__heading">Ingredients</h3>
                <ul className="rm-list">
                  {recipe.ingredients.map((item, i) => (
                    <li key={i} className="rm-list__item">{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {recipe.instructions && recipe.instructions.length > 0 && (
              <div className="rm-section">
                <h3 className="rm-section__heading">Method of Preparation</h3>
                <ol className="rm-list">
                  {recipe.instructions.map((step, i) => (
                    <li key={i} className="rm-list__item">{step}</li>
                  ))}
                </ol>
              </div>
            )}

            {recipe.nutritiveValues && recipe.nutritiveValues.length > 0 && (
              <div className="rm-section rm-section--last">
                <h3 className="rm-section__heading">Nutritive Values</h3>
                <table className="rm-nutrition-table">
                  <thead>
                    <tr>
                      <th>Nutrients</th>
                      <th>Per Sample</th>
                      <th>Per 100g</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipe.nutritiveValues.map((row, i) => (
                      <tr key={i}>
                        <td>{row.nutrient}</td>
                        <td>{row.perSample}</td>
                        <td>{row.per100g}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          </div>
        </div>

        {/* ── Share ─────────────────────────── */}
        <div className="rm-share">
          <span className="rm-share__label">Share</span>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rm-share__btn rm-share__btn--whatsapp"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp
          </a>
        </div>

      </div>
    </div>
  );
};

export default RecipeModal;
